"use client"
import { CopyOutlined, SyncOutlined } from "@ant-design/icons";
import { Button, ConfigProvider, DatePicker, Form, Input, message, Modal, Select } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { TopUpTransactionModel } from "@/core/models/top-up-transaction.model";
import { TopUpTransactionRepo } from "@/core/repos/tables/top-up-transaction.repo";
import { OBJECT_TYPES, objectTypePipe } from "@/core/pipes/object-type.pipe";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { invoiceStatusPipe } from "@/core/pipes/invoice-status.pipe";
import { usePermissionStore } from "@/core/stores/permission.store";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { useDrafts } from '@/core/stores/drafts.store';
import { ResetDraftButton } from '@/app/home/components/drafts/ResetDraftButton';
import { DEF_DATE_FORMAT } from "@/app/home/utils/global.utils";

const topUpTransactionRepo = container.get(TopUpTransactionRepo);

interface DetailModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: TopUpTransactionModel,
}

const INPUT_WIDTH = 450;

export const DetailModal: React.FC<DetailModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const permissionStore = usePermissionStore();
    const [form] = Form.useForm<TopUpTransactionModel>();
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [messageApi, contextHolder] = message.useMessage();    
    // Draft logic for TopUpTransactionModel
    const draft = useDrafts((state) => state.getDraft('TopUpTransactionModel'));
    const _saveDraft = useDrafts((state) => state.saveDraft);
    const saveDraft = () => _saveDraft('TopUpTransactionModel', form.getFieldsValue());
    const _clearDraft = useDrafts((state) => state.clearDraft);
    const clearDraft = () => {
        _clearDraft('TopUpTransactionModel');
        form.setFieldsValue(data);
    };
    // Attach hooks to handle data changes
    const handleDataChange = async () => {
        form.setFieldsValue(data);
        if (data.id && data.id.length > 0) {
            setIsEdit(true);
        } else {
            if (draft) form.setFieldsValue(draft);
            setIsEdit(false);
        }
    }
    useEffect(() => {
        handleDataChange();
    }, [data])

    // Initialize select options data
    const init = async () => {
        // await populateTollAreas();
    }
    // componentDidMount equivalent, run exclusively on client-side
    useEffect(() => {init()}, []);


    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            if (isEdit) {
                formData.id = data.id;
                const result = await topUpTransactionRepo.update({...data, ...formData});
                setIsModalOpen(false);
                if (result) {
                    messageApi.success({
                        content: "Đã cập nhật yêu cầu"
                    })
                } else {
                    messageApi.error({
                        content: "Cập nhật yêu cầu thất bại"
                    })
                }
            } else {
                const result = await topUpTransactionRepo.create({...data, ...formData});
                setIsModalOpen(false);
                clearDraft();
                if (result) {
                    messageApi.success({
                        content: "Thêm yêu cầu mới thành công"
                    })
                } else {
                    messageApi.error({
                        content: "Thêm yêu cầu mới thất bại"
                    })
                }
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };
    const handleCancel = () => {
      if (!isEdit) saveDraft();
      setIsModalOpen(false);
    };

    const handleCopy = (value: string) => {
        navigator.clipboard.writeText(value).then(() => {
          message.success('Đã sao chép vào bộ nhớ tạm!');
        }).catch(() => {
          message.error('Sao chép thất bại!');
        });
      };
      

    return (
        <ConfigProvider theme={MODAL_THEME}>
            {contextHolder}
            <Modal open={isModalOpen} onCancel={handleCancel} width={isEdit?1000:520}
                centered
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={isEdit?[]:[
                    <SaveButton key="save" isEdit={isEdit} loading={loading} onClick={handleSave}/>,
                ]}
            >
                <div className="flex flex-row w-full items-center justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">THÔNG TIN CHI TIẾT</p>
                    {!isEdit && <ResetDraftButton onClick={clearDraft} />}
                </div>
                <div className="flex flex-row justify-center mt-6 mb-2">
                    {isEdit?<Form<TopUpTransactionModel>
                        name="basic"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                        disabled={!permissionStore.page.edit}
                    >
                        <div className="flex">
                            <div className="flex flex-col w-1/2 pr-4">
                                <Form.Item<TopUpTransactionModel>
                                    label="Người nộp tiền"
                                    name="remitterName"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled
                                        addonAfter={
                                            <Button type="text" style={{height: 30}} icon={<CopyOutlined />} onClick={() => handleCopy(form.getFieldValue("remitterName"))}></Button>
                                        }
                                    ></Input>
                                </Form.Item>

                                <Form.Item<TopUpTransactionModel>
                                    label="Người tạo"
                                    name="createdBy"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled
                                        addonAfter={
                                            <Button type="text" style={{height: 30}} icon={<CopyOutlined />} onClick={() => handleCopy(form.getFieldValue("createdBy"))}></Button>
                                        }
                                    ></Input>
                                </Form.Item>

                                <Form.Item<TopUpTransactionModel>
                                    label="Số tiền giao dịch"
                                    name="amount"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>

                                <Form.Item<TopUpTransactionModel>
                                    label="Thông tin giao dịch"
                                    name="information"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                >
                                    <Input.TextArea style={{width: INPUT_WIDTH+18, height: 202, resize: "none", marginRight: 0}} disabled></Input.TextArea>
                                </Form.Item>
                            </div>
                            <div className="flex flex-col">
                                <Form.Item<TopUpTransactionModel>
                                    label="Số tham chiếu"
                                    name="id"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled 
                                    addonAfter={
                                        <Button type="text" style={{height: 30}} icon={<CopyOutlined />} onClick={() => handleCopy(form.getFieldValue("id"))}></Button>
                                    }></Input>
                                </Form.Item>

                                <Form.Item<TopUpTransactionModel>
                                    label="Mã giao dịch"
                                    name="code"
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled
                                        addonAfter={
                                            <Button type="text" style={{height: 30}} icon={<CopyOutlined />} onClick={() => handleCopy(form.getFieldValue("code"))}></Button>
                                        }
                                    ></Input>
                                </Form.Item>

                                <Form.Item<TopUpTransactionModel>
                                    label="Đối tượng giao dịch"
                                    name="objectType"
                                    rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                                    getValueProps={(i) => ({value: objectTypePipe(i)})}
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>

                                <Form.Item<TopUpTransactionModel>
                                    label="Trạng thái giao dịch"
                                    name="status"
                                    getValueProps={(i) => ({value: invoiceStatusPipe(i)})}
                                >
                                    <Input style={{width: INPUT_WIDTH}} disabled></Input>
                                </Form.Item>

                                <Form.Item<TopUpTransactionModel>
                                    label="Thời gian giao dịch"
                                    name="createdAt"
                                    getValueProps={(i)=>({value: dayjsPipe(i)})}
                                >
                                    <DatePicker format={DEF_DATE_FORMAT} disabled style={{width: INPUT_WIDTH}}></DatePicker>
                                </Form.Item>

                                <Form.Item<TopUpTransactionModel>
                                    label="Thời gian hoàn thành"
                                    name="completeDate"
                                    getValueProps={(i)=>({value: dayjsPipe(i)})}
                                >
                                    <DatePicker format={DEF_DATE_FORMAT} disabled style={{width: INPUT_WIDTH}}></DatePicker>
                                </Form.Item>
                            </div>
                        </div>
                    </Form>: <Form<TopUpTransactionModel>
                        name="basic"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                    >
                        <Form.Item<TopUpTransactionModel>
                            label="Đối tượng giao dịch"
                            name="objectType"
                            rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                        >
                            <Select placeholder="Chọn đối tượng" style={{width: INPUT_WIDTH}}>
                                {OBJECT_TYPES.map(type => <Select.Option key={type} value={type}>{objectTypePipe(type)}</Select.Option>)}
                            </Select>
                        </Form.Item>

                        <Form.Item<TopUpTransactionModel>
                            label="Người nộp tiền"
                            name="remitterName"
                            rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                        >
                            <Input style={{width: INPUT_WIDTH}}></Input>
                        </Form.Item>

                        <Form.Item<TopUpTransactionModel>
                            label="Số tiền giao dịch"
                            name="amount"
                            rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                        >
                            <Input style={{width: INPUT_WIDTH}}></Input>
                        </Form.Item>

                        <Form.Item<TopUpTransactionModel>
                            label="Thông tin giao dịch"
                            name="information"
                            rules={[{ required: true, message: 'Trường này là bắt buộc!' }]}
                        >
                            <Input.TextArea style={{width: INPUT_WIDTH, height: 202, resize: "none"}}></Input.TextArea>
                        </Form.Item>
                    </Form>}
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    )
}